import {InsightEvidence, type InsightEvidence as InsightEvidenceValue} from "@devplaninc/codeup";
import {isRecord} from "@/app/components/generative-markdown/directive";

type EvidenceDirectivePayload = {
  id?: string;
  items: unknown[];
};

export type LocalEvidenceGroup = {
  id?: string;
  evidence: InsightEvidenceValue[];
};

export function parseEvidenceDirectiveItems(source: string): InsightEvidenceValue[] {
  return parseEvidenceDirectiveGroup(source)?.evidence ?? [];
}

export function parseEvidenceDirectiveGroup(source: string): LocalEvidenceGroup | undefined {
  const payload = parseEvidenceDirectivePayload(source);
  return evidenceDirectivePayloadToGroup(payload);
}

export function parseEvidenceDirectiveGroupJSON(source: unknown): LocalEvidenceGroup | undefined {
  const payload = parseEvidenceDirectivePayloadJSON(source);
  return evidenceDirectivePayloadToGroup(payload);
}

function evidenceDirectivePayloadToGroup(payload: EvidenceDirectivePayload | undefined): LocalEvidenceGroup | undefined {
  if (!payload) return undefined;

  const evidence: InsightEvidenceValue[] = [];
  for (const item of payload.items) {
    const parsed = parseEvidenceDirectiveItem(item);
    if (parsed) evidence.push(parsed);
  }
  if (evidence.length === 0) return undefined;
  return {id: payload.id, evidence};
}

function parseEvidenceDirectivePayload(source: string): EvidenceDirectivePayload | undefined {
  let json: unknown;
  try {
    json = JSON.parse(source);
  } catch {
    return undefined;
  }
  if (!isRecord(json) || !Array.isArray(json.items)) return undefined;
  return {id: localEvidenceId(json), items: json.items};
}

function parseEvidenceDirectivePayloadJSON(json: unknown): EvidenceDirectivePayload | undefined {
  if (!isRecord(json) || !Array.isArray(json.items)) return undefined;
  return {id: localEvidenceId(json), items: json.items};
}

function parseEvidenceDirectiveItem(item: unknown): InsightEvidenceValue | undefined {
  if (!isRecord(item)) return undefined;

  const candidate = decodeEvidenceItem(item);
  if (!candidate) return undefined;

  const hasUsableRef = Boolean(candidate.ref?.target);
  const note = candidate.note.trim();
  if (hasUsableRef) return candidate;
  if (!note) return undefined;
  return {...candidate, ref: undefined, note};
}

function decodeEvidenceItem(item: Record<string, unknown>): InsightEvidenceValue | undefined {
  try {
    return item.ref !== undefined
      ? InsightEvidence.fromJSON({...item, ref: normalizeEvidenceRefJSON(item.ref)})
      : InsightEvidence.fromJSON({
        ref: normalizeEvidenceRefJSON(item),
        note: item["note"],
        createdAt: item["createdAt"] ?? item["created_at"],
      });
  } catch {
    const note = typeof item["note"] === "string" ? item["note"].trim() : "";
    return note ? {ref: undefined, note, createdAt: undefined} : undefined;
  }
}

function normalizeEvidenceRefJSON(ref: unknown): unknown {
  if (!isRecord(ref)) return ref;
  if (!isRecord(ref.issue)) return ref;
  return {
    ...ref,
    issue: {
      ...ref.issue,
      tracker: normalizeIssueTracker(ref.issue.tracker),
    },
  };
}

function normalizeIssueTracker(tracker: unknown): unknown {
  if (typeof tracker !== "string") return tracker;
  const normalized = tracker.trim().toUpperCase();
  if (normalized === "JIRA") return "ISSUE_TRACKER_JIRA";
  if (normalized === "LINEAR") return "ISSUE_TRACKER_LINEAR";
  return tracker;
}

function localEvidenceId(item: Record<string, unknown>): string | undefined {
  const value = item["id"] ?? item["evidenceId"] ?? item["evidence_id"];
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}
